/*
 * How a refused request becomes something a person can read, and how the two refusals this area
 * acts on are recognised.
 *
 * The server never sends prose: it sends a locale key (protocol.md §1.1). This area's catalogue
 * holds the keys the auth routes can answer with; anything else falls back to the generic text of
 * the wire code, so an older client shows a sentence rather than a raw key.
 */

import { t, textOfKey } from "../i18n/index.ts"
import { WireError } from "../net/api.ts"

/** The tiers of security.md §4.5: 1 is a password re-entry, 2 additionally a fresh emailed code. */
export type StepUpTier = 1 | 2

/** The German of a failure, whatever threw it. */
export function errorText(err: unknown): string {
	if (!(err instanceof WireError)) return t("error.unknown")
	return (
		textOfKey(err.key, err.args) || textOfKey(`error.${err.code}`, err.args) || t("error.unknown")
	)
}

/**
 * The tier a refusal asks for, or null when it is not a step-up refusal. The server carries it as
 * `detail:{tier}` (protocol.md §4.10); a detail that says anything else means the password tier,
 * which is the one every step-up starts with anyway.
 */
export function stepUpTierOf(err: unknown): StepUpTier | null {
	if (!(err instanceof WireError) || err.code !== "stepUpRequired") return null
	const detail = err.detail
	if (typeof detail === "object" && detail !== null && "tier" in detail) {
		return detail.tier === 2 ? 2 : 1
	}
	return 1
}

/** How long a throttled caller has to wait, in whole seconds; 0 when the error names no delay. */
export function retryAfterSeconds(err: unknown): number {
	if (!(err instanceof WireError) || err.retryAfterMs === undefined) return 0
	return Math.max(0, Math.ceil(err.retryAfterMs / 1000))
}

/** True while the failure is the throttle of security.md §8 rather than a wrong input. */
export function isRateLimited(err: unknown): boolean {
	return err instanceof WireError && err.code === "rateLimited"
}

/**
 * True when a link is gone — spent, expired or never known. The server answers all three the same
 * way (a 410 with `error.auth.secretInvalid`, security.md §4.2), and so does the screen: it offers
 * the way to a new link instead of a retry that cannot work.
 */
export function isSecretSpent(err: unknown): boolean {
	return err instanceof WireError && (err.code === "gone" || err.key === "error.auth.secretInvalid")
}
